import * as Sentry from '@sentry/react'
import { logger } from '@tunasong/models'
import { useEffect, useState } from 'react'
import * as z from 'zod'
import { getSentryDsn, isAutomatedBrowser } from '../telemetry-config.js'
import { getSentryEnvironment } from './sentry-environment.js'
import { dropKnownNoise, sentryIgnoreErrors } from './sentry-filters.js'

/** Undefined for a local build or without a DSN, so Sentry never starts (GH #2334) */
const sentryDsn = getSentryDsn(import.meta.env)

const env = z
  .object({
    /** Build-time git SHA, defined by the app's vite config. Absent in dev. */
    VITE_BUILD_SHA: z.string().optional(),
  })
  .parse(import.meta.env)

export const sentryStyles = {
  '#sentry-feedback': {
    '--page-margin': '40px',
  },
}

export const useSentry = ({ mode, email, name }: { mode: 'light' | 'dark'; email?: string; name?: string }) => {
  const [initialized, setInitialized] = useState(false)
  useEffect(() => {
    if (!sentryDsn) {
      logger.debug('No Sentry DSN, or a local build - skipping Sentry initialization')
      return
    }
    if (isAutomatedBrowser(typeof navigator === 'undefined' ? undefined : navigator)) {
      logger.debug('Automated browser (navigator.webdriver) - skipping Sentry initialization')
      return
    }
    if (initialized) {
      return
    }

    /** @see https://docs.sentry.io/platforms/javascript/guides/react/user-feedback/ */

    // disable feedback for mobile devices since it obscures the UI
    // const feedbackIntegration = Sentry.feedbackIntegration({
    //   // Additional SDK configuration goes in here, for example:
    //   colorScheme: mode,

    //   useSentryUser: {
    //     email: 'email',
    //     name: 'name',
    //   },
    //   isNameRequired: false,
    // })

    Sentry.setUser({
      email,
      name,
    })
    Sentry.init({
      dsn: sentryDsn,
      /**
       * Without this the SDK defaults every event to `production`, so PR
       * previews reported as production and distorted issue counts,
       * `firstSeen`, and impact ranking (GH #1732). Derived from the hostname
       * at runtime because CI serves ONE built artifact from both the preview
       * bucket and production — see `getSentryEnvironment`.
       */
      environment: getSentryEnvironment(window.location.hostname),
      /**
       * Lets Sentry attribute a regression to a specific build. Omitted rather
       * than sent as a placeholder when the SHA is unavailable (dev server),
       * so a missing release is visible instead of silently wrong.
       */
      release: env.VITE_BUILD_SHA || undefined,
      // @todo we get Error: Multiple Sentry Session Replay instances are not supported
      integrations: [
        Sentry.browserTracingIntegration() /* feedbackIntegration */ /* Sentry.replayIntegration() */,
      ].filter(Boolean),

      /** Environment noise, by message. Never Slate's "Cannot resolve" errors: see `sentry-filters.ts` (GH #2686) */
      ignoreErrors: sentryIgnoreErrors,
      /** slate-react's teardown race, recognised by its stack; the same message from an event handler is reported */
      beforeSend: dropKnownNoise,

      /** @todo adjust the percentage of errors to be posted */
      tracesSampleRate: 1.0,
      /** @todo reduce to e.g., 0.1 when stable */
      replaysSessionSampleRate: 1.0, // This sets the sample rate at 100%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    })
    setInitialized(true)
  }, [email, initialized, name, mode])
}
